import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';

const Profile = () => {
  const { token, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user || !token) return;
      try {
        const res = await axios.get(`${API_URL}/users/${user._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(res.data);
      } catch (e) {
        console.error('Failed to load profile:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user, token]);

  const isOwner = profile?.role === 'Car Owner';

  const startEdit = () => {
    setErr(''); setMsg('');
    setForm({
      name: profile.name || profile.details?.name || '',
      businessName: profile.details?.businessName || '',
      phone: profile.details?.phone || '',
      address: profile.details?.address ||
        (typeof profile.details?.location === 'string' ? profile.details.location : ''),
      taxId: profile.details?.taxId || '',
    });
    setEditing(true);
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      const body = isOwner
        ? { name: form.name, phone: form.phone }
        : { businessName: form.businessName, phone: form.phone, address: form.address, taxId: form.taxId };
      const res = await axios.patch(`${API_URL}/users/${user._id}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(res.data.user || res.data);
      setEditing(false);
      setMsg('Profil mis à jour.');
    } catch (e) {
      setErr(e.response?.data?.message || 'Échec de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="p-6">Chargement du profil…</p>;
  if (!profile) return <p className="p-6">Profil introuvable.</p>;

  const label = 'block text-xs font-semibold text-[#8F877B] dark:text-[#6E665C] mb-1';
  const input = 'w-full rounded border border-[#2B2724] border-[#2B2724] bg-[#17140F] dark:bg-[#17140F] px-3 py-2 text-sm text-[#F4F1EA] text-[#F4F1EA] outline-none focus:ring-2 focus:ring-blue-500';
  const Row = ({ k, v }) => (
    <p><strong>{k} :</strong> {v || '—'}</p>
  );

  return (
    <div className="max-w-3xl mx-auto mt-10 p-6 bg-[#17140F] dark:bg-[#100F0D] shadow rounded">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-[#F4F1EA] text-[#F4F1EA]">Profil</h2>
        {!editing && (
          <button onClick={startEdit}
            className="rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2">
            Modifier
          </button>
        )}
      </div>

      {msg && <p className="mb-3 text-sm text-green-600">{msg}</p>}
      {err && <p className="mb-3 text-sm text-red-600">{err}</p>}

      {editing ? (
        <div className="space-y-3">
          {isOwner ? (
            <div>
              <label className={label}>Nom</label>
              <input className={input} name="name" value={form.name} onChange={onChange} />
            </div>
          ) : (
            <div>
              <label className={label}>Nom de l'entreprise</label>
              <input className={input} name="businessName" value={form.businessName} onChange={onChange} />
            </div>
          )}

          <div>
            <label className={label}>Téléphone</label>
            <input className={input} name="phone" value={form.phone} onChange={onChange} />
          </div>

          {!isOwner && (
            <>
              <div>
                <label className={label}>Adresse</label>
                <input className={input} name="address" value={form.address} onChange={onChange} />
              </div>
              <div>
                <label className={label}>N° TVA</label>
                <input className={input} name="taxId" value={form.taxId} onChange={onChange} />
              </div>
            </>
          )}

          <div className="flex items-center gap-2 pt-2">
            <button onClick={save} disabled={saving}
              className="rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button onClick={() => setEditing(false)} disabled={saving}
              className="rounded border border-[#2B2724] border-[#2B2724] text-sm px-4 py-2 text-[#C9C3B6] text-[#F4F1EA]">
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-sm text-[#F4F1EA] text-[#F4F1EA]">
          <Row k="Nom" v={isOwner ? (profile.name || profile.details?.name) : profile.details?.businessName || profile.name} />
          <Row k="Email" v={profile.email} />
          <Row k="Téléphone" v={profile.details?.phone} />
          {!isOwner && (
            <Row k="Adresse" v={profile.details?.address ||
              (typeof profile.details?.location === 'string' ? profile.details.location : '')} />
          )}
          <Row k="Rôle" v={profile.role} />

          {!isOwner && (
            <>
              <Row k="N° TVA" v={profile.details?.taxId} />
              <Row k="TVA applicable" v={profile.tvaApplicable || profile.details?.tvaApplicable} />
            </>
          )}

          {profile.role === 'Car Workshop' && (
            <>
              <Row k="Spécialisations" v={profile.details?.specializations?.length
                ? profile.details.specializations.join(', ') : ''} />
              <Row k="Marques" v={
                Array.isArray(profile.details?.brands) ? profile.details.brands.join(', ') : profile.details?.brands} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Profile;
